import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MODULES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, RefreshCw, Loader2, Building2, Power, PowerOff, Trash2, ShieldAlert, Download, TestTube2 } from "lucide-react";
import { toast } from "sonner";
import { useCompany } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";

const emptyModules = () => Object.fromEntries(MODULES.map((m) => [m.key, false]));
const allModulesOn = () => Object.fromEntries(MODULES.map((m) => [m.key, true]));

export default function Companies() {
  const { reload: reloadCompanies } = useCompany();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // company or null
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(null); // company to activate/deactivate

  const [form, setForm] = useState({
    name: "",
    logo_url: "",
    color: "#00A36C",
    modules: allModulesOn(),
    ai_credit_limit: null,
    requires_approval: true,
    is_demo: false,
    admin_email: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies/");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar empresas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", logo_url: "", color: "#00A36C", modules: allModulesOn(), ai_credit_limit: null, requires_approval: true, is_demo: false, admin_email: "" });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      logo_url: c.logo_url || "",
      color: c.color || "#00A36C",
      modules: { ...emptyModules(), ...(c.modules || {}) },
      ai_credit_limit: c.ai_credit_limit || null,
      requires_approval: c.requires_approval !== undefined ? c.requires_approval : true,
      is_demo: !!c.is_demo,
      admin_email: c.admin_email || "",
    });
    setDialogOpen(true);
  };

  const toggleModule = (key) =>
    setForm((f) => ({ ...f, modules: { ...f.modules, [key]: !f.modules[key] } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        logo_url: form.logo_url || null,
        color: form.color,
        modules: form.modules,
        ai_credit_limit: form.ai_credit_limit,
        requires_approval: form.requires_approval,
        is_demo: form.is_demo,
        admin_email: form.admin_email || null,
      };
      if (editing) {
        await api.patch(`/companies/${editing.id}`, payload);
        toast.success("Empresa actualizada");
      } else {
        await api.post("/companies/", payload);
        toast.success("Empresa creada");
      }
      setDialogOpen(false);
      await load();
      await reloadCompanies();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const doToggle = async () => {
    if (!confirmToggle) return;
    try {
      await api.patch(`/companies/${confirmToggle.id}`, { active: !confirmToggle.active });
      toast.success(confirmToggle.active ? "Empresa desactivada" : "Empresa reactivada");
      setConfirmToggle(null);
      await load();
      await reloadCompanies();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  // Fase 28 · Bug F · Exportar backup JSON de una empresa
  const exportCompany = async (c) => {
    try {
      toast.info(`Descargando backup de "${c.name}"…`);
      const { data } = await api.get(`/admin/export/company/${c.id}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `fstudy-backup-${c.name.replace(/[^a-zA-Z0-9]+/g, "_")}-${stamp}.json`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
      toast.success("Backup JSON descargado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al exportar");
    }
  };

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="companies-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
            <Building2 className="h-8 w-8 text-brand-emerald" />
            Empresas
          </h1>
          <p className="text-sm text-stone-500 mt-2 max-w-2xl">
            Administre las empresas (agencias / marcas / clientes) que su equipo opera.
            Cada empresa tiene sus propios módulos, cuentas y datos.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="refresh-companies-button">
            <RefreshCw className="h-4 w-4 mr-2" /> Recargar
          </Button>
          <DemoPurgeButton onDone={load} />
          <ResetToZeroButton onDone={load} />
          <Button
            onClick={openCreate}
            data-testid="create-company-button"
            className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold"
          >
            <Plus className="h-4 w-4 mr-2" /> Nueva Empresa
          </Button>
        </div>
      </div>

      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50/60">
              <TableHead>Empresa</TableHead>
              <TableHead>Módulos activos</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-16 text-stone-500">
                  Aún no hay empresas registradas.
                </TableCell>
              </TableRow>
            ) : (
              items.map((c) => {
                const enabledMods = MODULES.filter((m) => c.modules?.[m.key]);
                return (
                  <TableRow key={c.id} data-testid={`company-row-${c.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div
                          className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold"
                          style={{ backgroundColor: c.color || "#00A36C" }}
                        >
                          {c.initial || c.name?.[0]}
                        </div>
                        <div>
                          <div className="font-medium text-brand-ink">{c.name}</div>
                          <div className="text-xs text-stone-400 font-mono">{c.id.slice(0, 8)}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5 max-w-md">
                        {enabledMods.length === 0 ? (
                          <span className="text-xs text-stone-400">—</span>
                        ) : (
                          enabledMods.map((m) => (
                            <Badge key={m.key} className="bg-stone-100 text-stone-700 border border-stone-200 font-medium">
                              {m.label}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {c.active ? (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge className="bg-brand-emerald-soft text-brand-emerald-dark border border-brand-emerald/20">
                            Activa
                          </Badge>
                          {c.is_demo && (
                            <Badge className="bg-brand-gold-soft text-brand-gold-dark border border-brand-gold/40 text-[10px]" data-testid={`demo-badge-${c.id}`}>
                              DEMO
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <Badge className="bg-stone-100 text-stone-500 border border-stone-200">Inactiva</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => exportCompany(c)}
                          data-testid={`export-company-${c.id}`}
                          title="Exportar JSON"
                          className="text-brand-emerald hover:bg-brand-emerald-soft"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {c.is_demo && (
                          <DemoCompanyCleanupButton company={c} onDone={load} />
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEdit(c)}
                          data-testid={`edit-company-${c.id}`}
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setConfirmToggle(c)}
                          data-testid={`toggle-company-${c.id}`}
                          className={c.active ? "text-destructive hover:bg-destructive/10" : "text-brand-emerald-dark hover:bg-brand-emerald-soft"}
                          title={c.active ? "Desactivar" : "Reactivar"}
                        >
                          {c.active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="company-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {editing ? "Editar empresa" : "Nueva empresa"}
            </DialogTitle>
            <DialogDescription>
              Configure la identidad y los módulos habilitados para esta empresa.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">
                  Nombre
                </Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="company-form-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">
                  Color de marca
                </Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="h-10 w-16 p-1 cursor-pointer"
                    data-testid="company-form-color"
                  />
                  <Input
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="flex-1"
                    placeholder="#00A36C"
                  />
                </div>
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">
                  URL del logo (opcional)
                </Label>
                <Input
                  value={form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  placeholder="/assets/logo.png o URL absoluta"
                  data-testid="company-form-logo"
                />
              </div>
            </div>

            {/* Fase 29 · email administrativo para facturación */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold mb-1 block">
                Email administrativo (para facturas)
              </Label>
              <Input
                type="email"
                value={form.admin_email}
                onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                placeholder="administracion@empresa.com"
                data-testid="company-form-admin-email"
              />
              <p className="text-[10px] text-stone-500 mt-1">
                Destino por defecto al enviar facturas. Si se deja vacío, se usa el correo del Admin de la empresa.
              </p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold mb-3 block">
                Módulos habilitados
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {MODULES.map((m) => (
                  <div
                    key={m.key}
                    className="flex items-center justify-between rounded-md border border-border bg-white px-4 py-2.5"
                  >
                    <span className="text-sm font-medium text-brand-ink">{m.label}</span>
                    <Switch
                      checked={!!form.modules[m.key]}
                      onCheckedChange={() => toggleModule(m.key)}
                      data-testid={`company-module-${m.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-md border border-brand-gold/40 bg-brand-gold-soft/30 p-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">
                  Límite de créditos IA (empresa)
                </Label>
                <label className="text-xs inline-flex items-center gap-1.5 text-stone-600">
                  <input
                    type="checkbox"
                    checked={!form.ai_credit_limit}
                    onChange={(e) => setForm({ ...form, ai_credit_limit: e.target.checked ? null : { value: 100, period: "month" } })}
                    data-testid="company-ai-unlimited"
                  />
                  Sin límite (ilimitado)
                </label>
              </div>
              {form.ai_credit_limit && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] text-stone-500">Valor</Label>
                    <Input
                      type="number" min={0}
                      value={form.ai_credit_limit.value}
                      onChange={(e) => setForm({ ...form, ai_credit_limit: { ...form.ai_credit_limit, value: Number(e.target.value) || 0 } })}
                      data-testid="company-ai-limit-value"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-stone-500">Periodo</Label>
                    <select
                      className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                      value={form.ai_credit_limit.period}
                      onChange={(e) => setForm({ ...form, ai_credit_limit: { ...form.ai_credit_limit, period: e.target.value } })}
                      data-testid="company-ai-limit-period"
                    >
                      <option value="month">Mensual</option>
                      <option value="week">Semanal</option>
                    </select>
                  </div>
                </div>
              )}
              <p className="text-[10px] text-stone-500 mt-2">
                1 generación de imagen = 1 crédito. Al llegar al 100% se bloquean las generaciones para toda la empresa.
              </p>
            </div>

            {/* Fase 25 · Flujo de aprobación */}
            <div className="space-y-2 border-t pt-4">
              <div className="text-xs uppercase tracking-widest font-semibold text-stone-600">Flujo de aprobación</div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.requires_approval}
                  onChange={(e) => setForm({ ...form, requires_approval: e.target.checked })}
                  data-testid="company-requires-approval"
                  className="mt-1"
                />
                <span>
                  <strong>Requerir aprobación</strong> · Las piezas generadas quedan pendientes hasta que Master o Admin las aprueben.
                  Desactivar para auto-aprobar todo (el Master siempre auto-aprueba, sin importar el flag).
                </span>
              </label>
            </div>

            {/* Fase 28 · Bug F · Flag is_demo (protección de datos reales) */}
            <div className="space-y-2 border-t pt-4">
              <div className="text-xs uppercase tracking-widest font-semibold text-stone-600 flex items-center gap-2">
                <TestTube2 className="h-3.5 w-3.5 text-brand-gold-dark" /> Empresa de pruebas
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.is_demo}
                  onChange={(e) => setForm({ ...form, is_demo: e.target.checked })}
                  data-testid="company-is-demo"
                  className="mt-1"
                />
                <span>
                  <strong>Marcar como demo</strong> · Se separa visualmente de las empresas reales
                  y habilita la limpieza individual desde &ldquo;Limpieza de datos de prueba&rdquo;. Empresas
                  con conexiones sociales reales no se pueden eliminar automáticamente.
                </span>
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="company-form-submit"
                className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold"
              >
                {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear empresa"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Toggle Confirmation Dialog */}
      <Dialog open={!!confirmToggle} onOpenChange={(v) => !v && setConfirmToggle(null)}>
        <DialogContent data-testid="toggle-company-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {confirmToggle?.active ? "Desactivar empresa" : "Reactivar empresa"}
            </DialogTitle>
            <DialogDescription>
              {confirmToggle?.active
                ? `¿Está seguro de desactivar “${confirmToggle?.name}”? Los miembros perderán acceso pero los datos (posts, campañas, piezas creativas) permanecen intactos.`
                : `¿Reactivar “${confirmToggle?.name}”? Los miembros recuperarán acceso inmediato con los datos previos.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmToggle(null)}>
              Cancelar
            </Button>
            <Button
              onClick={doToggle}
              data-testid="toggle-company-confirm"
              className={confirmToggle?.active
                ? "bg-destructive text-white hover:bg-destructive/90"
                : "bg-brand-emerald text-white hover:bg-brand-emerald-dark"}
            >
              {confirmToggle?.active ? "Sí, desactivar" : "Sí, reactivar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Fase 13 · Botón + diálogo doble-confirmación para eliminar datos de demostración.
// Solo visible para el rol Master. Requiere escribir literalmente "ELIMINAR"
// para habilitar el botón final.
// -----------------------------------------------------------------------------
function DemoPurgeButton({ onDone }) {
  const { isMaster } = useAuth();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const openDialog = async () => {
    setConfirmText("");
    setPreview(null);
    setOpen(true);
    try {
      const { data } = await api.get("/admin/demo-preview");
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/admin/demo-data", {
        headers: { "X-Confirm-Purge": "YES-DELETE-DEMO" },
      });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Se eliminaron ${total} documentos de demostración`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!isMaster) return null;

  return (
    <>
      <Button
        variant="outline"
        onClick={openDialog}
        data-testid="purge-demo-btn"
        className="border-destructive/40 text-destructive hover:bg-destructive/10"
      >
        <Trash2 className="h-4 w-4 mr-2" /> Eliminar datos demo
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="purge-demo-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <ShieldAlert className="h-5 w-5 text-destructive" />
              </div>
              <DialogTitle className="font-serif-display text-2xl text-brand-ink">
                Eliminar datos de demostración
              </DialogTitle>
            </div>
            <DialogDescription>
              Esta acción elimina TODOS los datos generados automáticamente por el seed
              (Empresa Demo completa, publicaciones/campañas/piezas/marketplace/messenger seed).
              <strong> NO se toca ningún dato creado por usuarios reales.</strong>
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="rounded-lg border border-border bg-stone-50/50 p-3 max-h-64 overflow-y-auto">
              <div className="text-xs uppercase tracking-widest font-semibold text-stone-500 mb-2">
                Documentos que serán eliminados
              </div>
              <ul className="space-y-1 text-sm">
                {Object.entries(preview).map(([k, v]) => (
                  <li key={k} className="flex justify-between">
                    <span className="text-stone-600">{k.replace(/_/g, " ")}</span>
                    <span className={`font-mono ${v > 0 ? "text-destructive font-semibold" : "text-stone-400"}`}>{v}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
              Escriba <span className="font-mono">ELIMINAR</span> para confirmar
            </Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ELIMINAR"
              data-testid="purge-demo-confirm-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancelar</Button>
            <Button
              onClick={purge}
              disabled={busy || confirmText !== "ELIMINAR"}
              className="bg-destructive text-white hover:bg-destructive/90"
              data-testid="purge-demo-execute"
            >
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Eliminar definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// -----------------------------------------------------------------------------
// Fase 14 · Botón "Reiniciar a cero" — deja FStudy + los 4 usuarios + las llaves
// de /integraciones y borra TODO lo demás (posts, campañas, piezas, marketplace,
// mercadolibre, messenger, chat, credit_usage, notifications, audit_logs, etc.).
// Solo Master. Confirmación escribiendo "REINICIAR".
// -----------------------------------------------------------------------------
function ResetToZeroButton({ onDone }) {
  const { isMaster, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const openDialog = () => { setConfirmText(""); setOpen(true); };

  const execute = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/admin/reset-to-zero", {
        headers: { "X-Confirm-Reset": "YES-RESET-ZERO" },
      });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Sistema reiniciado. ${total} documentos eliminados. Vuelve a iniciar sesión.`);
      setOpen(false);
      // Como se borra audit_logs y potentially notifications, el frontend puede
      // quedar en estado inconsistente. Forzamos logout + reload para partir limpio.
      setTimeout(async () => { await logout(); window.location.href = "/login"; }, 400);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!isMaster) return null;

  return (
    <>
      <Button
        variant="outline"
        onClick={openDialog}
        data-testid="reset-to-zero-btn"
        className="border-destructive text-destructive hover:bg-destructive hover:text-white font-semibold"
      >
        <ShieldAlert className="h-4 w-4 mr-2" /> Reiniciar a cero
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="reset-to-zero-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <ShieldAlert className="h-5 w-5 text-destructive" />
              </div>
              <DialogTitle className="font-serif-display text-2xl text-brand-ink">
                Reiniciar a cero
              </DialogTitle>
            </div>
            <DialogDescription>
              Esta acción <strong>conserva</strong> la empresa FStudy, los 4 usuarios y las
              llaves de <em>Integraciones</em>. <strong>Borra</strong> todo el contenido
              operativo: publicaciones, campañas de email, piezas creativas, marketplace,
              MercadoLibre, mensajes, notificaciones, historial de auditoría, medidor de
              créditos y cualquier empresa adicional. Ideal para dejar el sistema como el primer día.
              <br /><br />
              <strong className="text-destructive">Esta acción NO se puede deshacer.</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
              Escriba <span className="font-mono">REINICIAR</span> para confirmar
            </Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="REINICIAR"
              data-testid="reset-to-zero-confirm-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancelar</Button>
            <Button
              onClick={execute}
              disabled={busy || confirmText !== "REINICIAR"}
              className="bg-destructive text-white hover:bg-destructive/90"
              data-testid="reset-to-zero-execute"
            >
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reiniciar a cero
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// -----------------------------------------------------------------------------
// Fase 28 · Bug F · Limpieza individual de una empresa demo. Requiere:
// - is_demo=true (impuesto en backend también)
// - Escribir literalmente el nombre exacto de la empresa
// - Sin conexiones sociales REALES (checkeado en backend)
// -----------------------------------------------------------------------------
function DemoCompanyCleanupButton({ company, onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const execute = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/companies/${company.id}/demo-cleanup`, {
        headers: { "X-Confirm-Company-Name": company.name },
      });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Empresa demo "${company.name}" eliminada (${total} documentos).`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button
        size="icon" variant="ghost"
        onClick={() => { setText(""); setOpen(true); }}
        data-testid={`demo-cleanup-btn-${company.id}`}
        title="Limpieza de datos de prueba"
        className="text-brand-gold-dark hover:bg-brand-gold-soft"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid={`demo-cleanup-dialog-${company.id}`}>
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="h-10 w-10 rounded-full bg-brand-gold-soft flex items-center justify-center">
                <TestTube2 className="h-5 w-5 text-brand-gold-dark" />
              </div>
              <DialogTitle className="font-serif-display text-2xl text-brand-ink">
                Limpieza de datos de prueba
              </DialogTitle>
            </div>
            <DialogDescription>
              Va a <strong>eliminar</strong> la empresa demo <strong>&ldquo;{company.name}&rdquo;</strong> con todo
              su contenido (posts, campañas, piezas, chats, agenda, etc.). Los datos de otras
              empresas <strong>no se tocan</strong>. Se recomienda exportar un backup JSON antes de
              proceder.
              <br /><br />
              <strong className="text-destructive">Esta acción NO se puede deshacer.</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
              Escriba el nombre EXACTO: <span className="font-mono">{company.name}</span>
            </Label>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={company.name}
              data-testid={`demo-cleanup-input-${company.id}`}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancelar</Button>
            <Button
              onClick={execute}
              disabled={busy || text !== company.name}
              className="bg-destructive text-white hover:bg-destructive/90"
              data-testid={`demo-cleanup-execute-${company.id}`}
            >
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Eliminar definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

